import axios from 'axios';
import { simulateRequest } from '../../utils/helpers';
import {
  CAFM_API_HOST,
  CAFM_API_PORT,
  CAFM_API_PROTOCOL,
  CAFM_API_ENTRY_ROUTE,
} from '../../config';
import ApiErrorModel from '../models/ApiErrorModel';
import { HTTP_STATUS_CODE } from '../../utils/constants';
import requestMiddleware, { RequestMiddleware } from './RequestMiddleware';
import AuthClientStore from '../../clientStore/AuthClientStore';
import PATH from '../../routes/paths';

let _isRefreshing = false;
let _refreshSubscribers = [];

function onRefreshed(newToken) {
  _refreshSubscribers.forEach((callback) => callback(newToken));
  _refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  _refreshSubscribers.push(callback);
}

function onRefreshFailed() {
  _refreshSubscribers.forEach((callback) => callback(null));
  _refreshSubscribers = [];
}

/**
 * Abstract request class
 */
class AbstractRequest {
  constructor({ useMock = false, mockResponse, mockRequestDuration = 0 } = {}) {
    this._useMock = useMock;
    if (useMock) {
      this._useMock = useMock;
      this._mockResponse = mockResponse;
      this._mockRequestDuration = mockRequestDuration;
    }
  }

  /**
   *
   * @returns {string}
   */
  get apiProtocol() {
    return CAFM_API_PROTOCOL;
  }

  /**
   *
   * @returns {string}
   */
  get apiHost() {
    return CAFM_API_HOST;
  }

  /**
   *
   * @returns {string}
   */
  get apiPort() {
    return CAFM_API_PORT;
  }

  get apiEntryRoute() {
    return CAFM_API_ENTRY_ROUTE;
  }

  /**
   *
   * @returns {string}
   */
  get apiUrl() {
    return `${this.apiProtocol}://${this.apiHost}${
      this.apiPort ? `:${this.apiPort}` : ''
    }${this.apiEntryRoute}`;
  }

  /**
   *
   * @returns {string}
   */
  get apiResource() {
    return '';
  }

  /**
   *
   * @returns {string}
   */
  get apiRoute() {
    return `${this.apiUrl}/${this.apiResource}`;
  }

  /**
   *
   * @returns {{}}
   */
  get routes() {
    return {};
  }

  /**
   * request wrapper
   *
   * @param method
   * @param url
   * @param data
   * @param extraConfig
   * @param requestType
   * @returns {Promise<AxiosResponse<any>|*>}
   */
  async request(
    method,
    url,
    data = {},
    extraConfig,
    requestType = RequestMiddleware.REQUEST_TYPES.ALL_MATTER
  ) {
    if (this._useMock) {
      await simulateRequest(this._mockRequestDuration);
      return this._mockResponse;
    }

    const requestConfig = {
      method,
      url,
      data,
      ...extraConfig,
    };

    const makeRequest = (requestConfigParameter) =>
      axios(requestConfigParameter);

    return requestMiddleware.apply(
      makeRequest,
      this.createAbortSource.bind(this),
      requestConfig,
      requestType
    );
  }

  /**
   *
   * @returns {AxiosInterceptorManager<AxiosRequestConfig>}
   */
  getRequestInterceptors() {
    return axios.interceptors.request;
  }

  /**
   *
   * @param handler
   * @returns {number}
   */
  addRequestInterceptor(handler) {
    return axios.interceptors.request.use(handler);
  }

  /**
   *
   * @param responseHandler
   * @param errorHandler
   */
  addResponseInterceptor(responseHandler, errorHandler) {
    return axios.interceptors.response.use(responseHandler, errorHandler);
  }

  /**
   *
   * @param {number} id
   */
  removeRequestInterceptor(id) {
    axios.interceptors.request.eject(id);
  }

  addGlobalErrorHandlingInterceptor() {
    this.addResponseInterceptor(
      (response) => response,
      (error) => {
        const errorCodeFormApi =
          error?.response?.data?.code?.toString() ||
          error?.response?.data?.message?.toString();

        if (error?.response?.status !== HTTP_STATUS_CODE.UNAUTHORIZED) {
          ApiErrorModel.showError(errorCodeFormApi);
        }

        return error;
      }
    );
  }

  addRefreshTokenInterceptor() {
    this.addResponseInterceptor(null, async (error) => {
      const originalRequest = error?.config;

      if (
        error?.response?.status !== HTTP_STATUS_CODE.UNAUTHORIZED ||
        !originalRequest ||
        originalRequest._retry ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/login')
      ) {
        return Promise.reject(error);
      }

      const refreshToken = AuthClientStore.getRefreshToken();
      if (!refreshToken) {
        if (window.location.pathname !== PATH.AUTH.LOGIN) {
          AuthClientStore.setReturnUrl(
            window.location.pathname + window.location.search
          );
          window.location.href = window.location.origin + PATH.AUTH.LOGIN;
        }
        return Promise.reject(error);
      }

      if (_isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              originalRequest._retry = true;
              resolve(axios(originalRequest));
            } else {
              reject(error);
            }
          });
        });
      }

      _isRefreshing = true;
      originalRequest._retry = true;

      try {
        // Lazy import to avoid circular dependency
        const { authRequest } = await import('./AuthRequest');
        const data = await authRequest.refreshAccessToken();
        const newToken = data.access_token;
        onRefreshed(newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        onRefreshFailed();
        AuthClientStore.removeRefreshToken();
        if (window.location.pathname !== PATH.AUTH.LOGIN) {
          AuthClientStore.setReturnUrl(
            window.location.pathname + window.location.search
          );
          window.location.href = window.location.origin + PATH.AUTH.LOGIN;
        }
        return Promise.reject(refreshError);
      } finally {
        _isRefreshing = false;
      }
    });
  }

  createAbortSource() {
    return new AbortController();
  }
}

export default AbstractRequest;
